import React, { useState } from 'react';
import { Card, Icon } from '@design-manager-inc/ui';
import { Link } from 'react-router-dom';
import { routes } from 'app/features/Routing/Routes';
import type { IMoodBoard } from 'api/resources/moodBoards';
import { MoodBoardStatus } from './MoodBoardStatus';
import { MoodBoardButtons } from './MoodBoardButtons';

interface IMoodBoardCardProps {
	moodBoard: IMoodBoard;
	// The client's access token: re-appended to the board link (it is not
	// inherited from the current location) and handed to the status buttons.
	token: string;
}

const MoodBoardThumbnail = ({ src }: { src: string | null }) => {
	const [hasError, setHasError] = useState(false);

	if (!src || hasError) {
		return (
			<div
				data-testid="mood-board-thumbnail-placeholder"
				className="w-24 h-24 md:w-32 md:h-32 shrink-0 flex items-center justify-center rounded-lg bg-backgroundGray"
			>
				<Icon name="square-3-stack-3d" size="lg" className="fill-gray-400" />
			</div>
		);
	}

	return (
		// Mood board thumbnails are direct blob/CDN URLs — unlike item images they
		// are not served through `${PUBLIC_APP_API_BASE_URL}/file/${token}/...`.
		<img
			src={src}
			alt=""
			loading="lazy"
			onError={() => setHasError(true)}
			className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg object-cover bg-backgroundGray"
		/>
	);
};

export const MoodBoardCard = ({ moodBoard, token }: IMoodBoardCardProps) => {
	const to = `/${routes.moodboard.getRoute({ id: moodBoard.id })}?token=${token}`;

	// The link covers the thumbnail and title only: the approve / reject
	// buttons sit beside it rather than inside it, so pressing one never also
	// opens the board (and no button ends up nested in an anchor).
	return (
		<Card
			border="2xl"
			className="w-full p-5 bg-background shadow-lg transition-shadow hover:shadow-xl"
		>
			<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
				<Link
					to={to}
					className="grow flex flex-row items-center gap-4"
					data-testid="mood-board-card-link"
				>
					<MoodBoardThumbnail src={moodBoard.thumbnailUrl ?? null} />

					<h3 className="text-lg font-normal text-primary-badge">
							{moodBoard.title || 'Mood Board'}
						</h3>
				</Link>

				<div className="flex flex-col items-center gap-2 md:flex-row md:justify-end">
					<MoodBoardStatus status={moodBoard.approvalStatus} />
					<MoodBoardButtons moodBoard={moodBoard} token={token} />
				</div>
			</div>
		</Card>
	);
};

MoodBoardCard.displayName = 'MoodBoardCard';
